import { ChangeEvent, forwardRef } from 'react';

import { addStyles } from '../../utils/helpers';

export type TextAreaChangeEventHandler = (event: ChangeEvent<HTMLTextAreaElement>) => void;

const TextAreaStyles = {
  default: 'border rounded-t-lg rounded-bl-lg rounded-br-sm bg-white text-gray-900 resize max-w-full',
  hover: 'hover:border-jungle-500',
  focus: 'focus:shadow-[0_0_4px_0_rgba(9,138,127,0.5)]',
  disabled: 'disabled:bg-gray-100 disabled:text-gray-300',
  placeholder: 'placeholder:text-gray-600',
  error: 'border-red-600',
  small: 'min-h-13.5',
  xsmall: 'min-h-11.5',
};

const TextAreaSizes = {
  default: 3,
  small: 2,
  xsmall: 1,
  medium: 5,
  large: 7,
};

export interface TextAreaProps
  extends React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> {
  onChange?: TextAreaChangeEventHandler;
  disabled?: boolean;
  placeholder?: string;
  error?: string;
  label?: string;
  size?: 'default' | 'small' | 'xsmall' | 'medium' | 'large';
  helperText?: string;
  name: string;
  value?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({ size = 'default', ...props }, ref) => {
  const { onChange, disabled, placeholder, error, label, helperText, name, value, ...rest } = props;
  return (
    <div className="flex flex-col text-gray-900">
      {label ? (
        <label className="flex flex-col pb-2">
          <span className="font-medium">{label}</span>
          {helperText && <span className="text-xs">{helperText}</span>}
        </label>
      ) : (
        <></>
      )}
      <textarea
        {...rest}
        ref={ref}
        value={value}
        className={addStyles([
          TextAreaStyles.default,
          error ? TextAreaStyles.error : 'border-gray-300',
          TextAreaStyles.focus,
          TextAreaStyles.hover,
          TextAreaStyles.disabled,
          TextAreaStyles.placeholder,
        ])}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        rows={TextAreaSizes[size]}
        name={name}
      />
      {error && <span className="pt-1 text-red-600">{error}</span>}
    </div>
  );
});

TextArea.displayName = 'TextArea';
export default TextArea;
